/**
 * Loyal Review — Guide de l'administrateur (Français)
 */

export default `# Loyal Review — Guide de l'administrateur

## Qu'est-ce que Loyal Review ?

Loyal Review est un parcours positif alternatif à l'intérieur de
Review Shield. Les clients qui évaluent l'expérience au-dessus du
Seuil d'avis public voient un écran de choix bienveillant :

- **Recevoir un cadeau de notre part** — un bon-cadeau est envoyé
  par courriel au client et devient utilisable lors de sa
  prochaine visite.
- **Continuer pour laisser un avis public** — le parcours Google
  actuel, absolument inchangé.

Loyal Review réutilise l'interface Review Shield existante, le
moteur de bons existant et le pipeline de courriels existant. Aucun
nouveau flux de bons, aucune nouvelle page d'administration, aucun
nouveau rapport.

---

## Flux commerçant

### 1. Créer un bon (flux existant)
Allez dans **Bons → Allouer**. Ajoutez les destinataires, les
valeurs et le mode de livraison exactement comme aujourd'hui.

### 2. Marquez le bon « Disponible pour Loyal Review »
Avant de cliquer sur **Ajouter à la liste**, cochez la nouvelle
case « Disponible pour Loyal Review ». Ce marquage transforme le
document en *modèle de récompense* que le parcours Review Shield
peut cloner. Le modèle lui-même n'est jamais utilisé — il existe
pour que vous puissiez le consulter, le modifier ou l'archiver
depuis la liste standard des Bons.

### 3. Activez Loyal Review sur la carte
Allez dans **Cartes → Paramètres de la carte → Review Shield**.
Sous le Seuil d'avis public, un nouveau bouton **Expérience
positive** apparaît avec deux options : Standard ou Loyal Review.

- Choisissez **Standard** pour conserver le comportement actuel
  (les clients positifs vont directement au parcours d'avis public).
- Choisissez **Loyal Review** puis sélectionnez une récompense
  dans le menu déroulant. Seuls les bons marqués « Disponible pour
  Loyal Review » y apparaissent.

### 4. Enregistrez

La carte bascule immédiatement sur le parcours Loyal Review la
prochaine fois qu'un client la scanne et attribue une note
supérieure ou égale au seuil.

---

## Expérience du client

1. Le client scanne la carte et évalue l'expérience.
2. Si la note est **égale ou supérieure** au Seuil d'avis public
   ET que Loyal Review est activé, le client voit l'écran de
   choix.
3. S'il choisit **Recevoir un cadeau**, il :
   - saisit son courriel ;
   - lit et accepte les Conditions générales du bon-cadeau (obligatoire) ;
   - reçoit son bon à l'écran et par courriel ;
   - est ensuite invité gentiment à laisser un avis public.
4. S'il choisit **Continuer pour laisser un avis public**, le
   parcours est identique au comportement Standard actuel.

Le client ne voit jamais aucune marque Loyal Review. Il voit de la
copie hospitalière : « Choisissez une option : Recevoir un cadeau
de notre part · Continuer pour laisser un avis public ».

---

## Règle de visite retour

Chaque bon émis via Loyal Review est estampillé d'un
\`redeemable_from\` correspondant au **début du JOUR calendaire
SUIVANT dans le fuseau horaire local de l'organisation**. Le
personnel qui tente de valider un bon Loyal Review le jour de
l'émission verra le message de refus \`NOT_YET_REDEEMABLE\`.

Les bons émis manuellement **ne sont pas affectés** — ils restent
utilisables immédiatement. La règle de visite retour ne s'applique
qu'aux émissions Loyal Review.

---

## Seuil d'avis public — un seul réglage, deux parcours

Le Seuil d'avis public que vous avez déjà configuré pour Review
Shield est la *seule source de vérité*. Loyal Review en hérite
automatiquement. Il n'y a pas de seuil séparé, pas de double
réglage, aucun risque de dérive entre les deux parcours.

---

## Conditions générales du bon-cadeau

Avant qu'un bon Loyal Review puisse être émis, le client DOIT
accepter explicitement les Conditions générales du bon-cadeau sur
l'écran courriel. L'acceptation est enregistrée avec :

- Horodatage
- Langue affichée (fr / en / es)
- Version des conditions (\`v1.0\` au lancement)
- Source = \`LR\`

Si la case n'est pas cochée :

- Aucun bon n'est émis.
- Aucun courriel n'est envoyé.
- Rien n'est conservé au-delà du journal d'audit de la requête.

---

## Courriel de rappel

Si le client n'a pas utilisé son bon à la moitié de sa durée de
vie, un courriel de rappel automatique lui est envoyé — une seule
fois — via le pipeline Resend existant.

---

## Notes pour le personnel de soutien

- Les bons émis via Loyal Review affichent une source **LR** dans
  les rapports et l'audit des bons. Filtrez par cette source pour
  compter les émissions Loyal Review.
- Si un client signale « le bon ne fonctionne pas le jour où je
  l'ai reçu », confirmez la règle de visite retour ci-dessus —
  c'est intentionnel.
- Si un commerçant demande « comment désactiver Loyal Review »,
  il peut soit remettre la carte en **Standard** dans les
  paramètres, soit archiver / désactiver le bon-récompense (les
  deux arrêtent les nouvelles émissions).
`;
